import React, { useMemo, useRef } from 'react';
import useResizeObserver from 'Hooks/useResizeObserver';
import ReactECharts from 'echarts-for-react';
import get from 'lodash/get';
import {
  CHART_COLORS,
  INSIGHT_CHART_FONT_CHAR_WIDTH,
} from 'utils/constants/dashboard';
import { DonutChartProps } from '../chartBuilder';
import { CHART_EMPTY_STATE_COLORS } from '../constants/chartColors';
import { donutEmptyState } from '../constants/chartEmptyStates';
import { InsightDrilldownSelectedData } from '../../../types/store/normalized/insights/insightFilterMetadata';

const GenericDonutChart: React.FC<DonutChartProps> = ({
  chartData,
  onDrillDown,
  categoryFieldName,
  valueFieldName,
}) => {
  const containerRef = useRef<any>();
  const { height, width } = useResizeObserver(containerRef);

  const onEvents = useMemo(
    () => ({
      click: (item: {
        seriesType?: string;
        dataIndex?: number;
        seriesIndex?: number;
      }) => {
        let data: InsightDrilldownSelectedData | undefined;

        if (
          categoryFieldName &&
          item.dataIndex !== undefined &&
          chartData.length > 0
        ) {
          const chartItem = chartData[item.dataIndex];
          const fieldValue = chartItem[categoryFieldName];

          if (fieldValue) {
            data = {
              filters: { fieldName: categoryFieldName, fieldValue },
              chartData: chartItem,
            };
          }
        }

        onDrillDown && onDrillDown(undefined, data);
      },
    }),
    [chartData, categoryFieldName, onDrillDown]
  );

  const options = useMemo(() => {
    // Handle empty data case
    if (chartData.length === 0) {
      return donutEmptyState(width, height);
    }

    let fullLegendWidth =
      Math.max(
        ...chartData.map(
          (i) => get(i, categoryFieldName, '').toLocaleString().length
        )
      ) * INSIGHT_CHART_FONT_CHAR_WIDTH;
    let diameter = Math.min(width, height);
    let legendWidth = Math.min(width - diameter - 40, fullLegendWidth);
    let isLandscape = legendWidth >= Math.min(fullLegendWidth, 120);
    let leftSpace = (width - diameter - legendWidth - 40) / 2;

    return {
      grid: {
        show: false,
        containLabel: true,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
      },
      series: [
        {
          type: 'pie',
          radius: isLandscape
            ? [diameter / 4, diameter / 2 - 12]
            : [diameter / 4, diameter / 2 - 24],
          center: isLandscape
            ? [diameter / 2 + leftSpace, '50%']
            : ['50%', height / 2 - 16],
          itemStyle: {
            borderColor: CHART_EMPTY_STATE_COLORS.BORDER_WHITE,
            borderWidth: 1,
          },
          label: {
            show: false,
            position: 'center',
          },
          labelLine: {
            show: false,
          },
          data: chartData.map((i) => ({
            name: get(i, categoryFieldName),
            value: get(i, valueFieldName),
          })),
        },
      ],
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'cross',
        },
      },
      legend: {
        type: 'scroll',
        orient: isLandscape ? 'vertical' : 'horizontal',
        ...(isLandscape
          ? {
              top: 'center',
              left: diameter + leftSpace,
            }
          : {
              bottom: 0,
              left: 'center',
            }),
        icon: 'circle',
        textStyle: {
          width: isLandscape ? legendWidth : Math.min(fullLegendWidth, 120),
          overflow: 'truncate',
        },
        tooltip: {
          show: legendWidth < fullLegendWidth,
        },
      },
      textStyle: {
        fontFamily: 'inherit',
      },
      color: CHART_COLORS,
    };
  }, [chartData, width, height, categoryFieldName, valueFieldName]);

  return (
    <div className='h-full w-full' ref={containerRef}>
      {width > 0 && (
        <ReactECharts
          key={`${width}-${height}`}
          opts={{ renderer: 'svg' }}
          option={options}
          style={{ height: height, width: width }}
          onEvents={onEvents}
        />
      )}
    </div>
  );
};

export default GenericDonutChart;
